import { Form, Input, Modal, Select, TimePicker } from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import {
  IMeetingRoomQuery,
  IMeetingSchedule,
  IMeetingRoom,
} from "../../../models/Hrm";
import {
  useGetAllMeetingRoom,
  useUpdateMeetingSchedule,
} from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";
import { debounce } from "lodash";

const { RangePicker } = TimePicker;
const { Option } = Select;

interface IModalUpdateMeetingScheduleProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  editingMeeting: IMeetingSchedule | null;
}

const ModalUpdateMeetingSchedule: React.FC<
  IModalUpdateMeetingScheduleProps
> = ({ open, onCancel, onSuccess, editingMeeting }) => {
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<
    IMeetingRoomQuery & { page?: number; limit?: number }
  >({
    limit: 10,
    page: 1,
  });
  const [meetingRooms, setMeetingRooms] = useState<IMeetingRoom[]>([]);

  const updateMutation = useUpdateMeetingSchedule();
  const { data: roomsResponse, isFetching } = useGetAllMeetingRoom(filter);

  useEffect(() => {
    if (roomsResponse?.data) {
      if (filter.page === 1) {
        setMeetingRooms(roomsResponse.data);
      } else {
        setMeetingRooms((prev) => {
          const merged = [...prev];
          roomsResponse.data.forEach((item: IMeetingRoom) => {
            if (!merged.some((x) => x.id === item.id)) merged.push(item);
          });
          return merged;
        });
      }
    }
  }, [roomsResponse?.data, filter.page]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IMeetingRoomQuery, value?: string) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [setFilter],
  );

  const handleSearch = (field: keyof IMeetingRoomQuery) => (value?: string) => {
    debouncedSearch(field, value);
  };
  useEffect(() => {
    if (open && editingMeeting) {
      form.setFieldsValue({
        title: editingMeeting.title,
        timeRange: [
          dayjs(editingMeeting.startTime),
          dayjs(editingMeeting.endTime),
        ],
        meetingRooms: editingMeeting.meetingRooms?.id || undefined,
      });
    }
  }, [open, editingMeeting, form]);

  const handleEditSubmit = async (values: any) => {
    if (!editingMeeting) return;
    try {
      const [start, end] = values.timeRange;
      const targetDate = dayjs(editingMeeting.startTime);

      await updateMutation.mutateAsync({
        id: editingMeeting.id,
        data: {
          title: values.title,
          startTime: targetDate
            .hour(start.hour())
            .minute(start.minute())
            .second(0)
            .millisecond(0)
            .toISOString(),
          endTime: targetDate
            .hour(end.hour())
            .minute(end.minute())
            .second(0)
            .millisecond(0)
            .toISOString(),
          roomId: values.meetingRooms,
        },
      });
      notify.success("Cập nhật lịch họp thành công");
      onSuccess();
      form.resetFields();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      notify.error(err.response?.data?.message || "Cập nhật thất bại");
    }
  };

  return (
    <Modal
      title="Sửa lịch họp"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={updateMutation.isPending}
      destroyOnClose
    >
      <Form form={form} onFinish={handleEditSubmit} layout="vertical">
        <Form.Item
          name="title"
          label="Tiêu đề"
          rules={[{ required: true, message: "Vui lòng nhập tiêu đề" }]}
        >
          <Input placeholder="Nhập tiêu đề cuộc họp" />
        </Form.Item>
        <Form.Item
          name="timeRange"
          label="Thời gian"
          rules={[{ required: true, message: "Vui lòng chọn thời gian" }]}
        >
          <RangePicker
            format="HH:mm"
            style={{ width: "100%" }}
            placement="bottomLeft"
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode as HTMLElement
            }
          />
        </Form.Item>
        <Form.Item
          name="meetingRooms"
          label="Phòng họp"
          rules={[{ required: true, message: "Vui lòng chọn phòng họp" }]}
        >
          <Select
            placeholder="Chọn phòng họp"
            showSearch
            filterOption={false}
            onSearch={handleSearch("name")}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                Math.round(target.scrollTop + target.offsetHeight) >=
                  target.scrollHeight - 10 &&
                !isFetching
              ) {
                if (
                  roomsResponse?.pagination &&
                  (filter.page || 1) * (filter.limit || 10) <
                    roomsResponse.pagination.total
                ) {
                  setFilter((prev: any) => ({
                    ...prev,
                    page: (prev.page || 1) + 1,
                  }));
                }
              }
            }}
          >
            {meetingRooms.map((room: IMeetingRoom) => (
              <Option key={room.id} value={room.id}>
                {room.name} - {room.floor}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(ModalUpdateMeetingSchedule);
